import { cx } from "@uui/utils/cx"

/**
 * A section on the sheet: hairline card, condensed head with an optional mono
 * note on the right, hairline under the head. Give it an `id` when the section
 * is an anchor target: it gains scroll margin and accepts programmatic focus so
 * fragment navigation moves the sequential-focus point along with the viewport.
 */
export function Panel({
  id,
  title,
  description,
  note,
  trailing,
  headingLevel: Heading = "h2",
  children,
  className,
}: {
  id?: string
  title: string
  description?: React.ReactNode
  /** A one-line mono aside, right of the title: "three slots, set by the program". */
  note?: React.ReactNode
  trailing?: React.ReactNode
  headingLevel?: "h2" | "h3"
  children: React.ReactNode
  className?: string
}) {
  return (
    <section
      id={id}
      tabIndex={id ? -1 : undefined}
      aria-label={title}
      className={cx(
        "card flex flex-col gap-3 px-5 py-4",
        id && "scroll-mt-24 outline-none",
        className,
      )}
    >
      <div className="border-secondary flex items-baseline justify-between gap-3 border-b pb-2.5">
        <div className="flex min-w-0 flex-col gap-0.5">
          <Heading
            className={cx(
              "text-primary",
              Heading === "h2" ? "text-md" : "text-sm font-semibold",
            )}
          >
            {title}
          </Heading>
          {description && (
            <p className="text-tertiary text-sm">{description}</p>
          )}
        </div>
        {note && <span className="notation shrink-0">{note}</span>}
        {trailing}
      </div>
      {children}
    </section>
  )
}

/** A label/value pair inside a `Fields` grid. */
export function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="eyebrow">{label}</dt>
      <dd className="text-secondary text-sm break-words">{children}</dd>
    </div>
  )
}

export function Fields({ children }: { children: React.ReactNode }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
      {children}
    </dl>
  )
}
